import React, { useState, useEffect } from "react";
import Login from "./pages/login";
import ForgotPassword from "./pages/forgotPassword.js";
import ResetPassword from "./pages/resetPassword.js";
import AuthAction from "./pages/authAction.js";
import DeleteAccount from "./pages/deleteAccount.js";

import Navbar from "./components/navbar.js";
import SideBar from "./components/sideBar.js";
import OnboardingWizard from "./components/OnboardingWizard.js";
import MerchantProfile from "./pages/merchant/merchantProfile/merchantProfile";

// Admin
import NewUsers from "./pages/admin/newUsers";
import UsersList from "./pages/admin/usersList";
import RepeatPurchaseRateGraph from "./pages/admin/graphs/repeatPurchaseRateGraph.js";
import CustomerChurnRateGraph from "./pages/admin/graphs/customerChurnRateGraph.js";
import ItemsSoldDetails from "./pages/admin/graphs/itemsSoldDetails.js";
import CustomerPurchaseDetails from "./pages/admin/graphs/customerPurchaseDetails.js";
import NPSChart from "./pages/admin/graphs/NPS.js";
import IncreasedMarginChart from "./pages/admin/graphs/increasedMarginChart.js";
import MerchantApprovals from "./pages/admin/merchantApprovals.js";
import AdminKpiDashboard from "./pages/admin/AdminKpiDashboard.js";

// Merchant
import Businesses from "./pages/merchant/business/businesses";
import AddEditBusiness from "./pages/merchant/business/addEditBusiness";
import Clients from "./pages/merchant/clients/clients";
import AddEditClients from "./pages/merchant/clients/addEditClients";
import Items from "./pages/merchant/items/items.js";
import AddEditItems from "./pages/merchant/items/addEditItems.js";
import PurchaseHistory from "./pages/merchant/purchaseHistory/purchaseHistory.js";
import AddEditPurchaseHistory from "./pages/merchant/purchaseHistory/addEditPurchaseHistory.js";
import ItemsPurchased from "./pages/merchant/items/itemsPurchased.js";
import AddEditItemsPurchased from "./pages/merchant/items/addEditItemsPurchased.js";
import ReceiptsHistoryList from "./pages/merchant/receiptsHistory/receiptsHistory.js";
import AddEditReceiptsHistory from "./pages/merchant/receiptsHistory/addEditReceiptsHistory.js";
import RevenueGraph from "./pages/merchant/graphs/revenueGraph.js";
import CloverCallback from "./pages/merchant/cloverCallback.js";
import SquareCallback from "./pages/merchant/squareCallback.js";
import Payments from "./pages/merchant/clover/payments.js";
import CloverItems from "./pages/merchant/clover/items.js";
import Orders from "./pages/merchant/clover/orders.js";
import Analytics from "./pages/merchant/clover/analytics.js";
import CustomerLoyalty from "./pages/merchant/loyalty/customerLoyalty.js";
import MerchantsData from "./pages/merchant/square/merchantsData.js";
import ValidatedRewards from "./pages/merchant/rewards/validatedRewards.js";
import BillingPage from "./pages/merchant/billing/BillingPage.js";
import BillingSuccessPage from "./pages/merchant/billing/BillingSuccessPage.js";
import BillingCancelPage from "./pages/merchant/billing/BillingCancelPage.js";
import ReferralPage from "./pages/merchant/referral/ReferralPage.js";
import GoogleReviewsPage from "./pages/merchant/googleReviews/GoogleReviewsPage.js";
import FlashDealsPage from "./pages/merchant/flashDeals/FlashDealsPage.js";

import MerchantCustomersPage from "./pages/merchant/customers/merchantCustomersPage.js";

import HomePage from "./components/homePage.js";
import { hydrateUserInfoFromFirebase } from "./actions/userActions";
import { useDispatch } from "react-redux";
import { firebaseAuth } from "./firebase/firebaseClient";
import RequireAuth from "./components/RequireAuth.js";
import RequireAdmin from "./components/RequireAdmin.js";
import LoyaltySettingsPage from "./components/loyaltySettings";

import { BrowserRouter as Router, Routes, Route, Outlet, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useToast, ToastContainer } from "./components/UI/toast.js";
import { ToastContext } from "./contexts/ToastContext.js";
import { captureReferralFromUrl } from "./utils/referralTracking.js";
import { configureGlobalAxios } from "./utils/configureAxios.js";

export { ToastContext };

configureGlobalAxios();

function MainLayout() {
  const [isSidebarVisible, setSidebarVisible] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      setSidebarVisible(window.innerWidth >= 700);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      <Navbar
        setSidebarVisible={setSidebarVisible}
        isSidebarVisible={isSidebarVisible}
      />
      <SideBar isSidebarVisible={isSidebarVisible} />
      <Outlet />
    </>
  );
}

function App() {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const { toasts, showToast, removeToast } = useToast();

  // Cookiebot banner
  useEffect(() => {
    const waitForCookiebot = () => {
      if (window.Cookiebot) {
        if (!window.Cookiebot.consent || window.Cookiebot.consent.given === false) {
          window.Cookiebot.show();
        }
      } else {
        setTimeout(waitForCookiebot, 100);
      }
    };

    waitForCookiebot();
  }, []);

  // Language initialization
  useEffect(() => {
    const languageOptions = ["en", "fr"];
    const savedLang = localStorage.getItem("lang");

    if (savedLang && languageOptions.includes(savedLang)) {
      i18n.changeLanguage(savedLang);
      return;
    }

    const browserLang = navigator.language.slice(0, 2);

    if (languageOptions.includes(browserLang)) {
      i18n.changeLanguage(browserLang);
    } else {
      i18n.changeLanguage("en");
    }
  }, [i18n]);

  useEffect(() => {
    captureReferralFromUrl();
  }, []);

  // Sync Redux `is_admin` from Firebase custom claims after refresh.
  useEffect(() => {
    const unsub = firebaseAuth.onAuthStateChanged((user) => {
      if (user) dispatch(hydrateUserInfoFromFirebase());
    });
    return () => unsub();
  }, [dispatch]);

  return (
    <ToastContext.Provider value={showToast}>
      <div className="wrapper">
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Login />} />
            <Route path="/login" element={<Login />} />
            <Route path="/sign-up/" element={<OnboardingWizard />} />
            <Route path="/forgot-password/" element={<ForgotPassword />} />
            <Route path="/reset-password/" element={<ResetPassword />} />
            <Route path="/auth-action" element={<AuthAction />} />
            <Route path="/delete/" element={<DeleteAccount />} />
            <Route path="/clover/callback" element={<CloverCallback />} />
            <Route path="/square/callback" element={<SquareCallback />} />
            <Route path="/billing" element={<Navigate to="/billing/" replace />} />
            <Route
              path="/billing/"
              element={
                <RequireAuth>
                  <BillingPage />
                </RequireAuth>
              }
            />
            <Route path="/billing/success" element={<BillingSuccessPage />} />
            <Route path="/billing/cancel" element={<BillingCancelPage />} />
            <Route
              path="/homepage"
              element={
                <RequireAuth>
                  <HomePage />
                </RequireAuth>
              }
            />
            <Route
              path="/merchant-approvals/"
              element={
                <RequireAdmin>
                  <MerchantApprovals />
                </RequireAdmin>
              }
            />
            <Route
              path="/admin-kpis/"
              element={
                <RequireAdmin>
                  <AdminKpiDashboard />
                </RequireAdmin>
              }
            />
            <Route
              path="/loyalty-settings/"
              element={
                <RequireAuth>
                  <LoyaltySettingsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/customers"
              element={
                <RequireAuth>
                  <MerchantCustomersPage />
                </RequireAuth>
              }
            />
            <Route
              path="/google-reviews/"
              element={
                <RequireAuth>
                  <GoogleReviewsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/flash-deals/"
              element={
                <RequireAuth>
                  <FlashDealsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/referral/"
              element={
                <RequireAuth>
                  <ReferralPage />
                </RequireAuth>
              }
            />
            <Route
              path="/merchant-profile/"
              element={
                <RequireAuth>
                  <MerchantProfile />
                </RequireAuth>
              }
            />

            {/* Protected Routes */}
            <Route element={<MainLayout />}>
              <Route path="/dashboard/" element={<Navigate to="/homepage" replace />} />

              {/* Admin Routes */}
              <Route path="/inactive-users/" element={<NewUsers />} />
              <Route path="/users-list/" element={<UsersList />} />

              {/* Business Routes */}
              <Route path="/businesses/" element={<Businesses />} />
              <Route path="/add-business/" element={<AddEditBusiness />} />
              <Route path="/edit-business/:id/" element={<AddEditBusiness />} />

              {/* Client Routes */}
              <Route path="/clients/" element={<Clients />} />
              <Route path="/add-client/" element={<AddEditClients />} />
              <Route path="/edit-client/:id/" element={<AddEditClients />} />

              {/* Item Routes */}
              <Route path="/items/" element={<Items />} />
              <Route path="/add-item/" element={<AddEditItems />} />
              <Route path="/edit-item/:id/" element={<AddEditItems />} />

              {/* Purchase History Routes */}
              <Route path="/purchase-history/" element={<PurchaseHistory />} />
              <Route path="/add-purchase-history/" element={<AddEditPurchaseHistory />} />
              <Route path="/edit-purchase-history/:id/" element={<AddEditPurchaseHistory />} />

              {/* Items Purchased Routes */}
              <Route path="/items-purchased/" element={<ItemsPurchased />} />
              <Route path="/add-item-purchased/" element={<AddEditItemsPurchased />} />
              <Route path="/edit-item-purchased/:id/" element={<AddEditItemsPurchased />} />

              {/* Receipts History Routes */}
              <Route path="/receipts-history/" element={<ReceiptsHistoryList />} />
              <Route path="/add-receipt-history/" element={<AddEditReceiptsHistory />} />
              <Route path="/edit-receipt-history/:id/" element={<AddEditReceiptsHistory />} />

              {/* Graph / Analytics Routes */}
              <Route path="/revenue-graph/" element={<RevenueGraph />} />
              <Route path="/repeat-purchase-rate-graph/" element={<RepeatPurchaseRateGraph />} />
              <Route path="/customer-churn-rate-graph/" element={<CustomerChurnRateGraph />} />
              <Route path="/items-sold-details/" element={<ItemsSoldDetails />} />
              <Route path="/customer-details/:clientId/" element={<CustomerPurchaseDetails />} />
              <Route path="/NPS/" element={<NPSChart />} />
              <Route path="/increasedMarginChart/" element={<IncreasedMarginChart />} />

              {/* Clover Routes */}
              <Route path="/clover-payments/" element={<Payments />} />
              <Route path="/clover-items/" element={<CloverItems />} />
              <Route path="/clover-orders/" element={<Orders />} />
              <Route path="/clover-analytics/" element={<Analytics />} />

              {/* Loyalty Routes */}
              <Route path="/customer-loyalty/" element={<CustomerLoyalty />} />
              <Route path="/validated-rewards/" element={<ValidatedRewards />} />

              {/* Square Routes */}
              <Route path="/square-merchants/" element={<MerchantsData />} />
            </Route>
          </Routes>
        </Router>

        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </ToastContext.Provider>
  );
}

export default App;
